import { api } from '../../../shared/lib/axios';
import { unwrapArray, unwrapObject, unwrapPaginated, type Paginated } from './envelope';

export type { Paginated };

/**
 * API client do módulo de Recrutamento e Seleção (change
 * `modulo-recrutamento-selecao`).
 *
 * O backend envelopa respostas em `{ version, _links, data }`; as
 * funções aqui já devolvem o conteúdo desembrulhado, para que os hooks e
 * as telas nunca precisem conhecer o envelope.
 */

// -------------------------------------------------------------------
// Tipos
// -------------------------------------------------------------------

export type JobRequisitionStatus =
  | 'rascunho'
  | 'em_aprovacao'
  | 'devolvida'
  | 'aprovada'
  | 'reprovada'
  | 'cancelada';

export type JobType = 'clt' | 'pj' | 'estagio' | 'temporario' | 'aprendiz';

export type ApprovalDecision = 'pendente' | 'aprovado' | 'reprovado' | 'devolvido' | 'encerrado';

export type JobOpeningStatus =
  | 'nao_publicada'
  | 'publicada'
  | 'despublicada'
  | 'preenchida'
  | 'encerrada';

export type WorkModel = 'presencial' | 'hibrido' | 'remoto';

export type ApplicationStatus =
  | 'em_analise'
  | 'entrevista_agendada'
  | 'aprovado'
  | 'reprovado'
  | 'banco_talentos';

export interface HiringReason {
  id: string;
  key: string;
  label: string;
  requiresReplacedEmployee: boolean;
}

export interface JobRequisitionBenefit {
  id?: string;
  description: string;
  estimatedValue?: string | null;
}

/** Cliente da vaga — só os campos que o módulo de recrutamento recebe. */
export interface RequisitionClient {
  id: string;
  code: string;
  razaoSocial: string;
  nomeFantasia?: string | null;
}

export interface JobRequisition {
  id: string;
  code: string;
  companyId: string;
  /** Nulo apenas em requisições anteriores ao campo. */
  clientId?: string | null;
  client?: RequisitionClient | null;
  costCenterId: string;
  requestingArea: string;
  positionTitle: string;
  vacanciesCount: number;
  jobType: JobType;
  hiringReasonId: string;
  hiringReason?: HiringReason;
  replacedEmployeeId?: string | null;
  replacedEmployeeName?: string | null;
  expectedSalary: string;
  justification: string;
  status: JobRequisitionStatus;
  requestedBy: string;
  submittedAt?: string | null;
  closedAt?: string | null;
  cancelReason?: string | null;
  returnCount: number;
  approvalCycle: number;
  benefits?: JobRequisitionBenefit[];
  company?: { id: string; tradeName?: string; corporateName?: string };
  costCenter?: { id: string; code: string; name: string };
  createdAt: string;
  updatedAt: string;
}

export interface RequisitionApprovalStep {
  id: string;
  stepKey: string;
  sortOrder: number;
  featureKey: string;
  decision: ApprovalDecision;
  decidedBy?: string | null;
  decidedAt?: string | null;
  parecer?: string | null;
  cycle: number;
}

export interface RequisitionHistoryEntry {
  id: string;
  event: string;
  stepKey?: string | null;
  decision?: string | null;
  parecer?: string | null;
  actorId?: string | null;
  cycle: number;
  createdAt: string;
}

export interface JobOpening {
  id: string;
  requisitionId: string;
  requisition?: JobRequisition;
  title?: string | null;
  description?: string | null;
  requirements?: string | null;
  city?: string | null;
  workModel?: WorkModel | null;
  status: JobOpeningStatus;
  publishedAt?: string | null;
  closedAt?: string | null;
  deadline?: string | null;
  disabledStageKeys: string[];
  approvedCount: number;
  recruiterId?: string | null;
  recruiter?: Recruiter | null;
}

/** Usuário interno que pode ser recrutador responsável de uma vaga. */
export interface Recruiter {
  id: string;
  name: string;
  email?: string | null;
}

export interface JobRequisitionDetail extends JobRequisition {
  approvals: RequisitionApprovalStep[];
  history: RequisitionHistoryEntry[];
  jobOpening: JobOpening | null;
}

export interface Candidate {
  id: string;
  fullName: string;
  cpf: string;
  /** `true` quando o CPF veio mascarado por falta de permissão. */
  cpfMasked: boolean;
  email: string;
  phone: string;
  linkedinUrl?: string | null;
  city?: string | null;
  expectedSalary?: string | null;
  positionOfInterest?: string | null;
  consentAt?: string | null;
  consentRevokedAt?: string | null;
  anonymizedAt?: string | null;
  createdAt: string;
}

export interface TalentPoolCandidate extends Candidate {
  lastStageKey?: string | null;
  lastClosingReason?: string | null;
}

export interface CandidateDocument {
  id: string;
  candidateId: string;
  type: 'curriculo' | 'outro';
  fileName: string;
  mimeType: string;
  sizeBytes: string;
  isCurrent: boolean;
  version: number;
  createdAt: string;
}

export interface Application {
  id: string;
  protocol: string;
  candidateId: string;
  candidate?: Candidate;
  jobOpeningId: string;
  jobOpening?: JobOpening;
  currentStageKey: string;
  status: ApplicationStatus;
  source: 'portal' | 'interno' | 'banco_talentos';
  closedStageKey?: string | null;
  closingReason?: string | null;
  closedAt?: string | null;
  lastMovedAt?: string | null;
  createdAt: string;
}

export interface ApplicationHistoryEntry {
  id: string;
  event: string;
  fromStageKey?: string | null;
  toStageKey?: string | null;
  status?: string | null;
  parecer?: string | null;
  score?: string | null;
  actorId?: string | null;
  timeInPreviousStageHours?: number | null;
  createdAt: string;
}

export interface ApplicationSchedule {
  id: string;
  applicationId: string;
  stageKey: string;
  scheduledAt: string;
  location?: string | null;
  meetingLink?: string | null;
  participants: string[];
  rescheduleCount: number;
  canceledAt?: string | null;
}

export interface SelectionStage {
  key: string;
  label: string;
  sortOrder: number;
  isRequired: boolean;
}

export interface FunnelBoard {
  jobOpeningId: string;
  stages: Array<{ key: string; label: string; count: number; applications: Application[] }>;
}

export interface FunnelIndicators {
  stages: Array<{
    key: string;
    label: string;
    current: number;
    advanced: number;
    rejected: number;
    approvalRate: number | null;
    averageHours: number | null;
  }>;
  totals: { applications: number; approved: number; rejected: number; talentPool: number };
}

export interface ListRequisitionsParams {
  search?: string;
  status?: JobRequisitionStatus;
  companyId?: string;
  costCenterId?: string;
  requestedBy?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface ListCandidatesParams {
  search?: string;
  city?: string;
  positionOfInterest?: string;
  salaryFrom?: string;
  salaryTo?: string;
  situation?: 'ativo' | 'banco_talentos' | 'sem_candidatura';
  orderBy?: 'createdAt' | 'lastMovedAt';
  orderDirection?: 'ASC' | 'DESC';
  page?: number;
  limit?: number;
}

export interface ListOpeningsParams {
  search?: string;
  status?: JobOpeningStatus;
  city?: string;
  companyId?: string;
  page?: number;
  limit?: number;
}

export interface CreateRequisitionPayload {
  companyId: string;
  clientId: string;
  costCenterId: string;
  requestingArea: string;
  positionTitle: string;
  vacanciesCount: number;
  jobType: JobType;
  hiringReasonId: string;
  replacedEmployeeId?: string;
  replacedEmployeeName?: string;
  expectedSalary: string;
  justification: string;
  benefits?: JobRequisitionBenefit[];
}

export interface DecisionPayload {
  decision: 'aprovar' | 'reprovar' | 'devolver';
  /** Passo que a tela acredita estar decidindo — distingue 409 de 403. */
  stepKey?: string;
  parecer?: string;
}

export interface PublishOpeningPayload {
  title: string;
  description: string;
  requirements?: string;
  city: string;
  workModel: WorkModel;
  deadline?: string;
  disabledStageKeys?: string[];
  /** `null` remove o recrutador definido. */
  recruiterId?: string | null;
}

export interface MoveApplicationPayload {
  targetStageKey?: string;
  parecer?: string;
  score?: number;
}

export interface SchedulePayload {
  scheduledAt: string;
  location?: string;
  meetingLink?: string;
  participants?: string[];
}

// -------------------------------------------------------------------
// Query keys
// -------------------------------------------------------------------

export const RECRUTAMENTO_KEYS = {
  all: ['recrutamento'] as const,
  requisicoes: (params?: ListRequisitionsParams) =>
    ['recrutamento', 'requisicoes', params] as const,
  requisicao: (id: string) => ['recrutamento', 'requisicao', id] as const,
  filaAprovacoes: (params?: ListRequisitionsParams) =>
    ['recrutamento', 'fila-aprovacoes', params] as const,
  vagas: (params?: ListOpeningsParams) => ['recrutamento', 'vagas', params] as const,
  vaga: (id: string) => ['recrutamento', 'vaga', id] as const,
  candidatos: (params?: ListCandidatesParams) => ['recrutamento', 'candidatos', params] as const,
  candidato: (id: string) => ['recrutamento', 'candidato', id] as const,
  candidatoDocumentos: (id: string) => ['recrutamento', 'candidato', id, 'documentos'] as const,
  bancoTalentos: (params?: ListCandidatesParams) =>
    ['recrutamento', 'banco-talentos', params] as const,
  etapas: () => ['recrutamento', 'etapas'] as const,
  painel: (openingId: string) => ['recrutamento', 'painel', openingId] as const,
  indicadores: (params?: Record<string, unknown>) =>
    ['recrutamento', 'indicadores', params] as const,
  candidatura: (id: string) => ['recrutamento', 'candidatura', id] as const,
  candidaturaHistorico: (id: string) => ['recrutamento', 'candidatura', id, 'historico'] as const,
  candidaturaAgendamentos: (id: string) =>
    ['recrutamento', 'candidatura', id, 'agendamentos'] as const,
  motivosContratacao: () => ['recrutamento', 'motivos-contratacao'] as const,
  clientesParaVaga: () => ['recrutamento', 'clientes-para-vaga'] as const,
  recrutadores: () => ['recrutamento', 'recrutadores'] as const,
  candidatoCandidaturas: (id: string) => ['recrutamento', 'candidato', id, 'candidaturas'] as const,
};

// -------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------

function toParams(params?: object): Record<string, string> {
  const out: Record<string, string> = {};
  if (!params) return out;
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = String(value);
  }
  return out;
}

// -------------------------------------------------------------------
// Requisições
// -------------------------------------------------------------------

export async function listarRequisicoes(
  params?: ListRequisitionsParams,
): Promise<Paginated<JobRequisition>> {
  const { data } = await api.get('/recruitment/requisitions', { params: toParams(params) });
  return unwrapPaginated<JobRequisition>(data);
}

export async function buscarRequisicao(id: string): Promise<JobRequisitionDetail> {
  const { data } = await api.get(`/recruitment/requisitions/${id}`);
  return unwrapObject<JobRequisitionDetail>(data);
}

export async function listarFilaAprovacoes(
  params?: ListRequisitionsParams,
): Promise<Paginated<JobRequisition>> {
  const { data } = await api.get('/recruitment/requisitions/approvals/queue', {
    params: toParams(params),
  });
  return unwrapPaginated<JobRequisition>(data);
}

export async function criarRequisicao(payload: CreateRequisitionPayload): Promise<JobRequisition> {
  const { data } = await api.post('/recruitment/requisitions', payload);
  return unwrapObject<JobRequisition>(data);
}

export async function salvarRascunhoRequisicao(
  payload: CreateRequisitionPayload,
): Promise<JobRequisition> {
  const { data } = await api.post('/recruitment/requisitions/drafts', payload);
  return unwrapObject<JobRequisition>(data);
}

export async function atualizarRequisicao(
  id: string,
  payload: Partial<CreateRequisitionPayload>,
): Promise<JobRequisition> {
  const { data } = await api.patch(`/recruitment/requisitions/${id}`, payload);
  return unwrapObject<JobRequisition>(data);
}

export async function enviarRequisicao(id: string): Promise<JobRequisition> {
  const { data } = await api.post(`/recruitment/requisitions/${id}/submit`, {});
  return unwrapObject<JobRequisition>(data);
}

export async function cancelarRequisicao(id: string, reason: string): Promise<JobRequisition> {
  const { data } = await api.post(`/recruitment/requisitions/${id}/cancel`, { reason });
  return unwrapObject<JobRequisition>(data);
}

export async function decidirRequisicao(id: string, payload: DecisionPayload) {
  const { data } = await api.post(`/recruitment/requisitions/${id}/decision`, payload);
  return unwrapObject(data);
}

/** Clientes elegíveis para o seletor da requisição. */
export async function listarClientesParaVaga(): Promise<RequisitionClient[]> {
  const { data } = await api.get('/recruitment/client-options');
  return unwrapArray<RequisitionClient>(data);
}

/** Usuários elegíveis como recrutador responsável. */
export async function listarRecrutadores(): Promise<Recruiter[]> {
  const { data } = await api.get('/recruitment/recruiter-options');
  return unwrapArray<Recruiter>(data);
}

// -------------------------------------------------------------------
// Vagas
// -------------------------------------------------------------------

export async function listarVagas(params?: ListOpeningsParams): Promise<Paginated<JobOpening>> {
  const { data } = await api.get('/recruitment/openings', { params: toParams(params) });
  return unwrapPaginated<JobOpening>(data);
}

export async function buscarVaga(id: string): Promise<JobOpening> {
  const { data } = await api.get(`/recruitment/openings/${id}`);
  return unwrapObject<JobOpening>(data);
}

export async function publicarVaga(
  id: string,
  payload: PublishOpeningPayload,
): Promise<JobOpening> {
  const { data } = await api.post(`/recruitment/openings/${id}/publish`, payload);
  return unwrapObject<JobOpening>(data);
}

export async function atualizarVaga(
  id: string,
  payload: Partial<PublishOpeningPayload>,
): Promise<JobOpening> {
  const { data } = await api.patch(`/recruitment/openings/${id}`, payload);
  return unwrapObject<JobOpening>(data);
}

export async function despublicarVaga(id: string): Promise<JobOpening> {
  const { data } = await api.post(`/recruitment/openings/${id}/unpublish`, {});
  return unwrapObject<JobOpening>(data);
}

export async function encerrarVaga(id: string): Promise<JobOpening> {
  const { data } = await api.post(`/recruitment/openings/${id}/close`, {});
  return unwrapObject<JobOpening>(data);
}

// -------------------------------------------------------------------
// Candidatos
// -------------------------------------------------------------------

export async function listarCandidatos(
  params?: ListCandidatesParams,
): Promise<Paginated<Candidate>> {
  const { data } = await api.get('/recruitment/candidates', { params: toParams(params) });
  return unwrapPaginated<Candidate>(data);
}

export async function listarBancoTalentos(
  params?: ListCandidatesParams,
): Promise<Paginated<TalentPoolCandidate>> {
  const { data } = await api.get('/recruitment/candidates/talent-pool', {
    params: toParams(params),
  });
  return unwrapPaginated<TalentPoolCandidate>(data);
}

export async function buscarCandidato(id: string): Promise<Candidate> {
  const { data } = await api.get(`/recruitment/candidates/${id}`);
  return unwrapObject<Candidate>(data);
}

export interface CreateCandidatePayload {
  fullName: string;
  cpf: string;
  email: string;
  phone: string;
  linkedinUrl?: string;
  city?: string;
  expectedSalary?: string;
  positionOfInterest?: string;
}

export async function criarCandidato(payload: CreateCandidatePayload): Promise<Candidate> {
  const { data } = await api.post('/recruitment/candidates', payload);
  return unwrapObject<Candidate>(data);
}

export async function atualizarCandidato(
  id: string,
  payload: Partial<CreateCandidatePayload>,
): Promise<Candidate> {
  const { data } = await api.patch(`/recruitment/candidates/${id}`, payload);
  return unwrapObject<Candidate>(data);
}

export async function listarDocumentosCandidato(id: string): Promise<CandidateDocument[]> {
  const { data } = await api.get(`/recruitment/candidates/${id}/documents`);
  return unwrapArray<CandidateDocument>(data);
}

export async function enviarCurriculo(id: string, file: File): Promise<CandidateDocument> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post(`/recruitment/candidates/${id}/documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrapObject<CandidateDocument>(data);
}

/**
 * URL temporária do currículo. Não é link estável: expira, e por isso é
 * buscada no clique e não guardada em cache de página.
 */
export async function obterUrlCurriculo(
  candidateId: string,
  documentId: string,
): Promise<{ url: string; fileName: string; expiresInSeconds: number }> {
  const { data } = await api.get(
    `/recruitment/candidates/${candidateId}/documents/${documentId}/url`,
  );
  return unwrapObject(data);
}

export async function vincularCandidatoAVaga(
  candidateId: string,
  jobOpeningId: string,
  source?: 'interno' | 'banco_talentos',
): Promise<Application> {
  const { data } = await api.post(`/recruitment/candidates/${candidateId}/applications`, {
    jobOpeningId,
    source,
  });
  return unwrapObject<Application>(data);
}

/** Candidaturas do candidato, com a identificação da vaga e do cliente. */
export async function listarCandidaturasDoCandidato(candidateId: string): Promise<Application[]> {
  const { data } = await api.get(`/recruitment/candidates/${candidateId}/applications`);
  return unwrapArray<Application>(data);
}

export async function revogarConsentimento(id: string) {
  const { data } = await api.delete(`/recruitment/candidates/${id}/consent`);
  return unwrapObject(data);
}

// -------------------------------------------------------------------
// Processo seletivo
// -------------------------------------------------------------------

export async function listarEtapas(): Promise<SelectionStage[]> {
  const { data } = await api.get('/recruitment/applications/stages');
  return unwrapArray<SelectionStage>(data);
}

export async function buscarPainel(jobOpeningId: string): Promise<FunnelBoard> {
  const { data } = await api.get(`/recruitment/applications/board/${jobOpeningId}`);
  return unwrapObject<FunnelBoard>(data);
}

export async function buscarIndicadoresFunil(
  params?: Record<string, unknown>,
): Promise<FunnelIndicators> {
  const { data } = await api.get('/recruitment/applications/indicators/funnel', {
    params: toParams(params),
  });
  return unwrapObject<FunnelIndicators>(data);
}

export async function buscarCandidatura(id: string): Promise<Application> {
  const { data } = await api.get(`/recruitment/applications/${id}`);
  return unwrapObject<Application>(data);
}

export async function listarHistoricoCandidatura(
  id: string,
): Promise<ApplicationHistoryEntry[]> {
  const { data } = await api.get(`/recruitment/applications/${id}/history`);
  return unwrapArray<ApplicationHistoryEntry>(data);
}

export async function listarAgendamentos(id: string): Promise<ApplicationSchedule[]> {
  const { data } = await api.get(`/recruitment/applications/${id}/schedules`);
  return unwrapArray<ApplicationSchedule>(data);
}

export async function movimentarCandidatura(
  id: string,
  payload: MoveApplicationPayload,
): Promise<Application> {
  const { data } = await api.post(`/recruitment/applications/${id}/move`, payload);
  return unwrapObject<Application>(data);
}

export async function agendarEtapa(
  id: string,
  payload: SchedulePayload,
): Promise<ApplicationSchedule> {
  const { data } = await api.post(`/recruitment/applications/${id}/schedule`, payload);
  return unwrapObject<ApplicationSchedule>(data);
}

export async function aprovarCandidatura(id: string, parecer?: string): Promise<Application> {
  const { data } = await api.post(`/recruitment/applications/${id}/approve`, { parecer });
  return unwrapObject<Application>(data);
}

export async function reprovarCandidatura(id: string, reason: string): Promise<Application> {
  const { data } = await api.post(`/recruitment/applications/${id}/reject`, { reason });
  return unwrapObject<Application>(data);
}

export async function enviarParaBancoTalentos(
  id: string,
  reason?: string,
): Promise<Application> {
  const { data } = await api.post(`/recruitment/applications/${id}/talent-pool`, { reason });
  return unwrapObject<Application>(data);
}
